import {
  workflowEndsWithConditionBranch,
  getConditionBranchLaneCount,
  computeConditionBranchGraphWidth,
  BRANCH_GRID_GAP,
} from './workflowConditionUtils';
import { WORKFLOW_MAIN_CARD_WIDTH, WORKFLOW_MAIN_CARD_CENTER_X } from './workflowCardAnchors';

function parseTransformX(element) {
  if (!element) return 0;
  const transform = element.style?.transform || window.getComputedStyle(element).transform || '';
  if (transform === 'none') return 0;
  const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
  if (matrixMatch) {
    const parts = matrixMatch[1].split(',').map((value) => Number.parseFloat(value.trim()));
    return Number.isFinite(parts[4]) ? parts[4] : 0;
  }
  const translateMatch = transform.match(/translate\(([-\d.]+)px/);
  return translateMatch ? Number.parseFloat(translateMatch[1]) : 0;
}

function readCenterXFromAttribute(element, attributeName) {
  const raw = element?.getAttribute?.(attributeName);
  if (raw == null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function readNumericAttribute(element, attributeName) {
  return readCenterXFromAttribute(element, attributeName);
}

export function captureMainLaneAlignmentDebug(container) {
  if (!container) return null;

  const mainLaneWidth = readNumericAttribute(
    container.querySelector('[data-workflow-main-chain-width]'),
    'data-workflow-main-chain-width',
  ) ?? readNumericAttribute(
    container.querySelector('[data-workflow-action-lane-width]'),
    'data-workflow-action-lane-width',
  );

  const actionLaneWidth = readNumericAttribute(
    container.querySelector('[data-workflow-action-lane-width]'),
    'data-workflow-action-lane-width',
  );

  const mainLaneX = readNumericAttribute(
    container.querySelector('[data-workflow-main-lane-x]'),
    'data-workflow-main-lane-x',
  );

  const actionLaneCenterX = readNumericAttribute(
    container.querySelector('[data-workflow-action-lane-center-x]'),
    'data-workflow-action-lane-center-x',
  );

  const cardCenterXValues = [...container.querySelectorAll('[data-workflow-step-card]')]
    .filter((el) => !el.closest('[data-workflow-condition-lane]'))
    .map((el) => readNumericAttribute(el, 'data-workflow-card-center-x'))
    .filter((v) => v != null);

  const stepConnectorCenterXValues = [...container.querySelectorAll('[data-workflow-step-connector]')]
    .map((el) => readNumericAttribute(el, 'data-workflow-step-connector-center-x'))
    .filter((v) => v != null);

  const plusCenterXValues = [...container.querySelectorAll('[data-workflow-step-connector-plus-center]')]
    .map(readPlusCenterX)
    .filter((v) => v != null);

  const triggerMergeCenterX = readNumericAttribute(
    container.querySelector('[data-workflow-trigger-connector]'),
    'data-workflow-trigger-merge-center-x',
  );

  const triggerTrunkEndX = readNumericAttribute(
    container.querySelector('[data-workflow-trigger-connector]'),
    'data-workflow-trigger-trunk-end-x',
  );

  const triggerRowVisualCenterX = readNumericAttribute(
    container.querySelector('[data-workflow-trigger-connector]'),
    'data-workflow-trigger-row-visual-center-x',
  );

  const triggerRowVisualWidth = readNumericAttribute(
    container.querySelector('[data-workflow-graph-canvas-width]'),
    'data-workflow-graph-canvas-width',
  ) ?? readNumericAttribute(
    container.querySelector('[data-workflow-trigger-row]'),
    'data-workflow-row-width',
  );

  const mainGlobalCenterX = readNumericAttribute(
    container.querySelector('[data-workflow-action-lane]'),
    'data-workflow-main-global-center-x',
  ) ?? readNumericAttribute(
    container.querySelector('[data-workflow-main-chain-layer]'),
    'data-workflow-main-global-center-x',
  ) ?? mainLaneX;

  const mainChainLeft = readNumericAttribute(
    container.querySelector('[data-workflow-action-lane]'),
    'data-workflow-main-chain-left',
  ) ?? readNumericAttribute(
    container.querySelector('[data-workflow-action-lane]'),
    'data-workflow-action-lane-offset',
  );

  const expectedLocalCenter = WORKFLOW_MAIN_CARD_CENTER_X;
  const localWithin = (value) => value != null && Math.abs(value - expectedLocalCenter) <= 1;
  const globalWithin = (a, b) => a != null && b != null && Math.abs(a - b) <= 1;

  const hasCardConnectorMismatch = [
    ...cardCenterXValues,
    ...stepConnectorCenterXValues,
    ...plusCenterXValues,
  ].some((value) => value != null && !localWithin(value));

  const hasTriggerMainLaneMismatch = triggerMergeCenterX != null
    && mainGlobalCenterX != null
    && !globalWithin(triggerMergeCenterX, mainGlobalCenterX);

  const hasLaneCardCenterMismatch = hasCardConnectorMismatch || hasTriggerMainLaneMismatch;

  const didMainLaneShrinkBelowCardWidth = (mainLaneWidth != null && mainLaneWidth < WORKFLOW_MAIN_CARD_WIDTH)
    || (actionLaneWidth != null && actionLaneWidth < WORKFLOW_MAIN_CARD_WIDTH);

  const firstStepConnectorCenterX = stepConnectorCenterXValues[0] ?? actionLaneCenterX;
  const mainGlobalCardCenterX = mainGlobalCenterX ?? mainLaneX;
  const mainGlobalConnectorCenterX = mainChainLeft != null && stepConnectorCenterXValues[0] != null
    ? mainChainLeft + stepConnectorCenterXValues[0]
    : mainGlobalCardCenterX;
  const firstPlusGlobalCenterX = mainChainLeft != null && plusCenterXValues[0] != null
    ? mainChainLeft + plusCenterXValues[0]
    : null;
  const actionCardGlobalCenters = cardCenterXValues.map(
    (localX) => (mainChainLeft ?? 0) + localX,
  );
  const triggerToMainDelta = triggerMergeCenterX != null && mainGlobalCardCenterX != null
    ? Math.abs(triggerMergeCenterX - mainGlobalCardCenterX)
    : null;
  const triggerToActionCenterDelta = triggerTrunkEndX != null && firstStepConnectorCenterX != null
    ? Math.abs(triggerTrunkEndX - ((mainChainLeft ?? 0) + firstStepConnectorCenterX))
    : null;
  const didTriggerConnectorDetach = triggerToActionCenterDelta != null
    ? triggerToActionCenterDelta > 1
    : hasTriggerMainLaneMismatch;
  const didTriggerConnectorUseSqueezedWidth = (mainLaneWidth != null && mainLaneWidth < WORKFLOW_MAIN_CARD_WIDTH)
    || (triggerMergeCenterX != null && Math.abs(triggerMergeCenterX - WORKFLOW_MAIN_CARD_CENTER_X) > 1
      && triggerRowVisualCenterX != null
      && Math.abs(triggerRowVisualCenterX - WORKFLOW_MAIN_CARD_CENTER_X) <= 1);

  return {
    mainCardWidth: WORKFLOW_MAIN_CARD_WIDTH,
    mainLaneWidth,
    mainLaneX: mainGlobalCenterX ?? mainLaneX,
    actionLaneWidth,
    actionLaneCenterX,
    mainChainLeft,
    mainChainWidth: mainLaneWidth,
    triggerRowVisualWidth,
    triggerRowVisualCenterX,
    triggerTrunkEndX,
    firstStepConnectorCenterX,
    triggerToActionCenterDelta,
    didTriggerConnectorDetach,
    didTriggerConnectorUseSqueezedWidth,
    mainLocalCardCenterX: actionLaneCenterX,
    mainGlobalCardCenterX,
    mainLocalConnectorCenterX: firstStepConnectorCenterX,
    mainGlobalConnectorCenterX,
    firstPlusGlobalCenterX,
    actionCardGlobalCenters,
    triggerToMainDelta,
    stepConnectorCenterXValues,
    plusCenterXValues,
    cardCenterXValues,
    triggerMergeCenterX,
    hasLaneCardCenterMismatch,
    hasTriggerMainLaneMismatch,
    hasCardConnectorMismatch,
    didMainLaneShrinkBelowCardWidth,
  };
}

export function captureTriggerMainLaneAlignmentDebug(container, connectorLayout = null) {
  const laneDebug = captureMainLaneAlignmentDebug(container);
  if (!laneDebug) return null;

  const triggerConnector = container?.querySelector('[data-workflow-trigger-connector]');
  const visibleTriggerItemCenters = connectorLayout?.branchCenters
    ?? connectorLayout?.triggerCenters
    ?? [];

  return {
    triggerCount: connectorLayout?.triggerCenters?.length ?? 0,
    includesAddTriggerTile: connectorLayout?.includeAddTileConnector ?? false,
    visibleTriggerItemCenters,
    visibleTriggerRowCenterX: connectorLayout?.triggerRowVisualCenterX
      ?? laneDebug.triggerRowVisualCenterX,
    triggerMergeCenterX: laneDebug.triggerMergeCenterX,
    triggerTrunkEndX: laneDebug.triggerTrunkEndX,
    actionLaneCenterX: laneDebug.actionLaneCenterX,
    mainChainLeft: laneDebug.mainChainLeft,
    mainGlobalCardCenterX: laneDebug.mainGlobalCardCenterX,
    mainGlobalConnectorCenterX: laneDebug.mainGlobalConnectorCenterX,
    firstPlusGlobalCenterX: laneDebug.firstPlusGlobalCenterX,
    actionCardGlobalCenters: laneDebug.actionCardGlobalCenters,
    triggerToMainDelta: laneDebug.triggerToMainDelta,
    triggerToActionCenterDelta: laneDebug.triggerToActionCenterDelta,
    didTriggerConnectorDetach: laneDebug.didTriggerConnectorDetach,
    didTriggerConnectorUseSqueezedWidth: laneDebug.didTriggerConnectorUseSqueezedWidth,
    mainChainLeft: laneDebug.mainChainLeft,
    mainLocalCardCenterX: laneDebug.mainLocalCardCenterX,
    mainGlobalCardCenterX: laneDebug.mainGlobalCardCenterX,
    mainLocalConnectorCenterX: laneDebug.mainLocalConnectorCenterX,
    mainGlobalConnectorCenterX: laneDebug.mainGlobalConnectorCenterX,
    firstPlusGlobalCenterX: laneDebug.firstPlusGlobalCenterX,
    actionCardGlobalCenters: laneDebug.cardCenterXValues?.map(
      (localX) => (laneDebug.mainChainLeft ?? 0) + localX,
    ) ?? [],
    hasTriggerMainLaneMismatch: laneDebug.hasTriggerMainLaneMismatch,
    hasCardConnectorMismatch: laneDebug.hasCardConnectorMismatch,
  };
}

function parseCommaSeparatedNumbers(raw) {
  if (!raw) return [];
  return raw.split(',').map((value) => Number.parseFloat(value.trim())).filter((value) => Number.isFinite(value));
}

function readOverflowMode(element) {
  if (!element) return null;
  const attr = element.getAttribute('data-workflow-nested-graph-overflow-mode')
    ?? element.getAttribute('data-workflow-nested-wrapper-overflow-mode');
  if (attr) return attr;
  const className = element.className || '';
  if (className.includes('overflow-hidden')) return 'hidden';
  if (className.includes('overflow-x-auto')) return 'scroll';
  if (className.includes('overflow-visible')) return 'visible';
  return null;
}

function detectLaneOverlap(lanes, graphRootRect) {
  if (!lanes.length || !graphRootRect) return false;
  const boxes = lanes.map((lane) => lane.getBoundingClientRect());
  for (let leftIndex = 0; leftIndex < boxes.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < boxes.length; rightIndex += 1) {
      const left = boxes[leftIndex];
      const right = boxes[rightIndex];
      const overlap = Math.min(left.right, right.right) - Math.max(left.left, right.left);
      if (overlap > 2) return true;
    }
  }
  return false;
}

function detectNestedGraphEdgeVisibility(nestedCanvas, scrollContainer, graphRootRect) {
  if (!nestedCanvas || !graphRootRect) {
    return {
      nestedLeftEdgeVisible: false,
      nestedRightEdgeVisible: false,
      nestedIsHorizontallyClipped: true,
    };
  }

  const canvasRect = nestedCanvas.getBoundingClientRect();
  const scrollRect = scrollContainer?.getBoundingClientRect?.() ?? canvasRect;
  const overflowMode = readOverflowMode(scrollContainer);
  const leftVisible = canvasRect.left >= scrollRect.left - 2
    || overflowMode === 'visible'
    || overflowMode === 'scroll';
  const rightVisible = canvasRect.right <= scrollRect.right + 2
    || overflowMode === 'visible'
    || overflowMode === 'scroll';
  const nestedIsHorizontallyClipped = !leftVisible || !rightVisible;

  return {
    nestedLeftEdgeVisible: leftVisible,
    nestedRightEdgeVisible: rightVisible,
    nestedIsHorizontallyClipped,
  };
}

function readPlusCenterX(element) {
  const raw = element?.getAttribute?.('data-workflow-step-connector-plus-center');
  if (!raw) return null;
  const [x] = raw.split(',').map((value) => Number.parseFloat(value.trim()));
  return Number.isFinite(x) ? x : null;
}

function readElementCenterXRelativeToRoot(element, rootRect) {
  if (!element || !rootRect) return null;
  const rect = element.getBoundingClientRect();
  return rect.left - rootRect.left + rect.width / 2;
}

/** Extract X-coordinate signature from SVG path strings (ignore Y drift). */
export function buildConnectorHorizontalSignature(container) {
  const pathElements = [
    ...container.querySelectorAll('[data-workflow-connector-path]'),
    ...container.querySelectorAll('[data-workflow-proof-connector-path]'),
    ...container.querySelectorAll('path[data-workflow-connector-path]'),
  ];

  const xs = [];
  pathElements.forEach((pathEl) => {
    const d = pathEl.getAttribute('d') || '';
    const numbers = d.match(/-?\d*\.?\d+/g)?.map(Number) || [];
    numbers.forEach((value, index) => {
      if (index % 2 === 0 && Number.isFinite(value)) {
        xs.push(Math.round(value));
      }
    });
  });

  const connectorCenterXs = [
    ...container.querySelectorAll('[data-workflow-step-connector-center-x]'),
  ].map((el) => readCenterXFromAttribute(el, 'data-workflow-step-connector-center-x')).filter((value) => value != null);

  const triggerMergeCenterX = readCenterXFromAttribute(
    container.querySelector('[data-workflow-trigger-merge-center-x]'),
    'data-workflow-trigger-merge-center-x',
  );

  return [
    triggerMergeCenterX != null ? `merge:${Math.round(triggerMergeCenterX)}` : null,
    ...connectorCenterXs.map((value) => `conn:${Math.round(value)}`),
    ...xs.map((value) => `path:${value}`),
  ].filter(Boolean).join('|');
}

/**
 * Capture live workflow graph geometry from a proof/builder container.
 */
export function captureWorkflowProofGeometry(container) {
  if (!container) {
    return null;
  }

  const graphRoot = container.querySelector('[data-workflow-graph-coordinate-root]');
  const graphRootRect = graphRoot?.getBoundingClientRect?.() ?? null;
  const content = container.querySelector('[data-workflow-canvas-content]');

  const mainLaneX = readCenterXFromAttribute(
    container.querySelector('[data-workflow-main-lane-x]')
      ?? container.querySelector('[data-workflow-action-lane-center-x]'),
    'data-workflow-main-lane-x',
  ) ?? readCenterXFromAttribute(
    container.querySelector('[data-workflow-action-lane-center-x]'),
    'data-workflow-action-lane-center-x',
  );

  const triggerMergeCenterX = readCenterXFromAttribute(
    container.querySelector('[data-workflow-trigger-merge-center-x]'),
    'data-workflow-trigger-merge-center-x',
  );

  const actionCenters = [...container.querySelectorAll('[data-workflow-step-card]')]
    .filter((element) => !element.closest('[data-workflow-condition-lane]'))
    .map((element) => readElementCenterXRelativeToRoot(element, graphRootRect))
    .filter((value) => value != null);

  const branchActionCenters = [...container.querySelectorAll('[data-workflow-branch-step-card="true"]')]
    .map((element) => readElementCenterXRelativeToRoot(element, graphRootRect))
    .filter((value) => value != null);

  const topLevelBranchCanvas = [...container.querySelectorAll('[data-workflow-condition-branch-canvas]')]
    .find((element) => !element.closest('[data-workflow-nested-condition-branch]'));
  const topLevelBranchGrid = topLevelBranchCanvas?.querySelector('[data-workflow-condition-branch-grid]');
  const topLevelBranchLanes = topLevelBranchGrid
    ? [...topLevelBranchGrid.querySelectorAll('[data-workflow-condition-lane]')]
    : [...container.querySelectorAll('[data-workflow-condition-lane]')]
      .filter((lane) => !lane.closest('[data-workflow-nested-condition-branch]'));

  const branchLanes = topLevelBranchLanes;
  const branchCenters = branchLanes
    .map((lane) => {
      const laneCenter = lane.getAttribute('data-workflow-condition-lane-center-x');
      const laneRect = lane.getBoundingClientRect();
      if (graphRootRect) {
        return laneRect.left - graphRootRect.left + laneRect.width / 2;
      }
      return laneCenter != null ? Number(laneCenter) : null;
    })
    .filter((value) => value != null);

  const branchCanvas = topLevelBranchCanvas;
  const branchGraphWidth = readCenterXFromAttribute(branchCanvas, 'data-workflow-condition-graph-width')
    ?? readCenterXFromAttribute(branchCanvas, 'data-workflow-condition-branch-width');
  const branchLaneWidths = parseCommaSeparatedNumbers(
    branchCanvas?.getAttribute('data-workflow-condition-lane-widths'),
  );
  const effectiveBranchLaneWidths = parseCommaSeparatedNumbers(
    topLevelBranchGrid?.getAttribute('data-workflow-condition-effective-lane-widths'),
  ).length
    ? parseCommaSeparatedNumbers(topLevelBranchGrid?.getAttribute('data-workflow-condition-effective-lane-widths'))
    : branchLaneWidths;
  const branchGraphLeft = readCenterXFromAttribute(
    container.querySelector('[data-workflow-condition-branch-slot]'),
    'data-workflow-condition-graph-left',
  );
  const parentCenterX = readCenterXFromAttribute(branchCanvas, 'data-workflow-condition-parent-center-x');
  const visibleBranchLaneCount = branchLanes.length;
  const mainLaneActionCardCount = [...container.querySelectorAll('[data-workflow-step-card]')]
    .filter((element) => !element.closest('[data-workflow-condition-lane]')).length;

  const plusCenters = [...container.querySelectorAll('[data-workflow-step-connector-plus-center]')]
    .map(readPlusCenterX)
    .filter((value) => value != null);

  const triggerCenters = [...container.querySelectorAll('[data-workflow-trigger-card-shell]')]
    .map((element) => readElementCenterXRelativeToRoot(element, graphRootRect))
    .filter((value) => value != null);

  const branchEndCount = container.querySelectorAll('[data-workflow-condition-end]').length;
  const hasGlobalMainChainEnd = container.querySelector('[data-workflow-action-lane-end]') != null;
  const laneAlignment = captureMainLaneAlignmentDebug(container);

  const branchConnectorSegmentCounts = {};
  const branchPlusCounts = {};
  const branchLaneHeights = {};
  const branchIds = [];
  const nestedGraphWidthsByBranch = {};
  const widestNestedGraphWidthByBranch = {};
  topLevelBranchLanes.forEach((lane) => {
    const branchId = lane.getAttribute('data-workflow-condition-branch-id') || 'unknown';
    branchIds.push(branchId);
    branchConnectorSegmentCounts[branchId] = lane.querySelectorAll('[data-workflow-condition-lane-connector]').length;
    branchPlusCounts[branchId] = lane.querySelectorAll('[data-workflow-condition-plus]').length;
    branchLaneHeights[branchId] = lane.getBoundingClientRect?.().height ?? 0;
    const nestedCanvas = lane.querySelector('[data-workflow-nested-condition-branch] [data-workflow-condition-branch-canvas]');
    const nestedWidth = readCenterXFromAttribute(nestedCanvas, 'data-workflow-condition-graph-width') ?? 0;
    if (nestedWidth > 0) {
      nestedGraphWidthsByBranch[branchId] = nestedWidth;
      widestNestedGraphWidthByBranch[branchId] = nestedWidth;
    }
  });

  const nestedConditionBranches = [...container.querySelectorAll('[data-workflow-nested-condition-branch]')];
  const nestedConditionWrapper = container.querySelector('[data-workflow-nested-condition-wrapper="true"]');
  const parentBranchLane = nestedConditionWrapper?.closest('[data-workflow-condition-lane]');
  const nestedVisibleBranchLaneCount = nestedConditionBranches.reduce(
    (total, element) => total + element.querySelectorAll('[data-workflow-condition-lane]').length,
    0,
  );
  const didNestedConditionRenderBranchGraph = nestedConditionBranches.some(
    (element) => element.querySelectorAll('[data-workflow-condition-lane]').length >= 2,
  );
  const nestedBranchGraphWidth = nestedConditionBranches.reduce((max, element) => {
    const canvas = element.querySelector('[data-workflow-condition-branch-canvas]');
    const width = readCenterXFromAttribute(canvas, 'data-workflow-condition-graph-width') ?? 0;
    return Math.max(max, width);
  }, 0);
  const nestedBranchGraphHeight = nestedConditionBranches.reduce((max, element) => {
    const canvas = element.querySelector('[data-workflow-condition-branch-canvas]');
    return Math.max(max, canvas?.offsetHeight ?? 0);
  }, 0);
  const nestedGraphOverflowMode = nestedConditionBranches
    .map((element) => element.querySelector('[data-workflow-branch-graph-scroll]')
      ?.getAttribute('data-workflow-nested-graph-overflow-mode'))
    .find(Boolean) ?? null;
  const nestedWrapperOverflowMode = nestedConditionBranches
    .map((element) => element.getAttribute('data-workflow-nested-wrapper-overflow-mode')
      ?? readOverflowMode(element))
    .find(Boolean) ?? null;
  const parentBranchOverflowMode = readOverflowMode(parentBranchLane);
  const nestedGraphLeft = readCenterXFromAttribute(
    nestedConditionBranches[0]?.querySelector('[data-workflow-condition-branch-canvas]'),
    'data-workflow-nested-graph-left',
  );
  const nestedParentCenterX = readCenterXFromAttribute(
    nestedConditionBranches[0]?.querySelector('[data-workflow-condition-branch-canvas]'),
    'data-workflow-condition-parent-center-x',
  );
  const nestedTargetXLocalValues = nestedConditionBranches[0]
    ? [...nestedConditionBranches[0].querySelectorAll('[data-workflow-condition-lane]')]
      .map((lane) => readCenterXFromAttribute(lane, 'data-workflow-condition-lane-center-x'))
      .filter((value) => value != null)
    : [];
  const nestedEdgeVisibility = detectNestedGraphEdgeVisibility(
    nestedConditionBranches[0]?.querySelector('[data-workflow-condition-branch-canvas]'),
    nestedConditionBranches[0]?.querySelector('[data-workflow-branch-graph-scroll]'),
    graphRootRect,
  );
  const nestedIsClipped = nestedConditionBranches.some((element) => {
    const canvas = element.querySelector('[data-workflow-condition-branch-canvas]');
    if (!canvas) return false;
    return canvas.scrollWidth > canvas.clientWidth + 1
      || canvas.scrollHeight > canvas.clientHeight + 1;
  });
  const hasNestedGraphClipping = nestedIsClipped || nestedEdgeVisibility.nestedIsHorizontallyClipped;
  const hasNestedGraphOverlap = detectLaneOverlap(
    [...container.querySelectorAll('[data-workflow-nested-condition-branch] [data-workflow-condition-lane]')],
    graphRootRect,
  );
  const hasBranchLaneWidthTooSmall = effectiveBranchLaneWidths.some((laneWidth, index) => {
    const branchId = branchIds[index];
    const nestedWidth = widestNestedGraphWidthByBranch[branchId] ?? 0;
    return nestedWidth > 0 && laneWidth < nestedWidth - 1;
  });
  const expectedGraphWidthFromLanes = effectiveBranchLaneWidths.reduce(
    (sum, width, index) => sum + width + (index > 0 ? BRANCH_GRID_GAP : 0),
    0,
  );
  const parentBranchLaneHeight = parentBranchLane?.getBoundingClientRect?.().height ?? 0;
  const parentBranchLaneWidth = readCenterXFromAttribute(
    parentBranchLane,
    'data-workflow-condition-lane-width',
  ) ?? parentBranchLane?.getBoundingClientRect?.().width ?? 0;
  const parentBranchSpacerHeight = container.querySelector('[data-workflow-branch-graph-spacer]')?.offsetHeight ?? 0;
  const nestedConditionStepId = nestedConditionWrapper?.querySelector('[data-workflow-nested-condition-step-id]')
    ?.getAttribute('data-workflow-nested-condition-step-id')
    ?? nestedConditionBranches[0]?.getAttribute('data-workflow-nested-condition-step-id')
    ?? null;
  const parentBranchId = parentBranchLane?.getAttribute('data-workflow-condition-branch-id') ?? null;
  const hasDuplicateParentEndUnderNestedCondition = nestedConditionWrapper
    ? [...parentBranchLane?.children || []].some(
      (child) => child.getAttribute('data-workflow-condition-end') != null,
    )
    : false;
  const isParentEndBelowNestedGraph = !hasDuplicateParentEndUnderNestedCondition;

  const branchEndPositions = [...container.querySelectorAll('[data-workflow-condition-end]')]
    .map((element) => {
      const lane = element.closest('[data-workflow-condition-lane]');
      const branchId = element.getAttribute('data-workflow-condition-branch-id')
        || lane?.getAttribute('data-workflow-condition-branch-id')
        || 'unknown';
      const rect = element.getBoundingClientRect();
      const laneRect = lane?.getBoundingClientRect?.();
      return {
        branchId,
        top: graphRootRect ? rect.top - graphRootRect.top : rect.top,
        laneBottom: laneRect && graphRootRect ? laneRect.bottom - graphRootRect.top : null,
      };
    });

  const branchContinuationDebug = captureBranchLaneContinuationDebug(container);

  return {
    ...laneAlignment,
    mainLaneX,
    triggerMergeCenterX: triggerMergeCenterX ?? laneAlignment?.triggerMergeCenterX,
    actionCenters,
    branchActionCenters,
    branchCenters,
    branchGraphWidth,
    branchGraphLeft,
    branchLaneWidths,
    effectiveBranchLaneWidths,
    expectedGraphWidthFromLanes,
    parentCenterX,
    visibleBranchLaneCount,
    mainLaneActionCardCount,
    plusCenters,
    triggerCenters,
    branchEndCount,
    hasGlobalMainChainEnd,
    branchConnectorSegmentCounts,
    branchPlusCounts,
    branchLaneHeights,
    branchIds,
    branchEndPositions,
    nestedVisibleBranchLaneCount,
    didNestedConditionRenderBranchGraph,
    nestedBranchGraphWidth,
    nestedBranchGraphHeight,
    nestedGraphLeft,
    nestedParentCenterX,
    nestedSourceXLocal: nestedParentCenterX,
    nestedTargetXLocalValues,
    nestedGraphOverflowMode,
    nestedWrapperOverflowMode,
    parentBranchOverflowMode,
    parentBranchLaneWidth,
    nestedIsClipped,
    nestedIsHorizontallyClipped: nestedEdgeVisibility.nestedIsHorizontallyClipped,
    nestedLeftEdgeVisible: nestedEdgeVisibility.nestedLeftEdgeVisible,
    nestedRightEdgeVisible: nestedEdgeVisibility.nestedRightEdgeVisible,
    hasNestedGraphClipping,
    hasNestedGraphOverlap,
    hasBranchLaneWidthTooSmall,
    nestedGraphWidthsByBranch,
    widestNestedGraphWidthByBranch,
    parentBranchLaneHeight,
    parentBranchSpacerHeight,
    nestedConditionStepId,
    parentBranchId,
    hasDuplicateParentEndUnderNestedCondition,
    isParentEndBelowNestedGraph,
    connectorPathsHash: buildConnectorHorizontalSignature(container),
    graphTransformX: parseTransformX(content),
    graphRootWidth: graphRootRect?.width ?? 0,
    ...(branchContinuationDebug || {}),
  };
}

export function detectUnexpectedGlobalEndUnderBranches(container, steps) {
  if (!container || !workflowEndsWithConditionBranch(steps)) return false;
  return container.querySelector('[data-workflow-action-lane-end]') != null;
}

export function compareWorkflowProofGeometry(before, after, {
  existingActionCount = before?.actionCenters?.length ?? 0,
  existingTriggerCount = before?.triggerCenters?.length ?? 0,
  tolerance = 1,
} = {}) {
  if (!before || !after) {
    return {
      didMainLaneMove: false,
      didExistingActionsMoveHorizontally: false,
      didConnectorPathsChangeHorizontally: false,
      didGraphTransformChange: false,
      didTriggerConnectorBreak: false,
    };
  }

  const within = (a, b) => Math.abs(a - b) <= tolerance;

  const didMainLaneMove = !within(before.mainLaneX ?? 0, after.mainLaneX ?? 0)
    || !within(before.triggerMergeCenterX ?? 0, after.triggerMergeCenterX ?? 0);

  const didExistingActionsMoveHorizontally = before.actionCenters
    .slice(0, existingActionCount)
    .some((centerX, index) => !within(centerX, after.actionCenters[index] ?? centerX + 9999));

  const didConnectorPathsChangeHorizontally = before.connectorPathsHash !== after.connectorPathsHash;

  const didGraphTransformChange = !within(before.graphTransformX ?? 0, after.graphTransformX ?? 0);

  const triggerCountChanged = before.triggerCenters?.length !== after.triggerCenters?.length;
  const didTriggerConnectorBreak = triggerCountChanged
    ? after.triggerCenters.length === 0
    : before.triggerCenters
      ?.slice(0, existingTriggerCount)
      .some((centerX, index) => !within(centerX, after.triggerCenters[index] ?? centerX + 9999));

  const newActionCenterX = after.actionCenters.length > existingActionCount
    ? after.actionCenters[existingActionCount]
    : null;

  const duplicatedActionCenterX = after.actionCenters.length > existingActionCount + 1
    ? after.actionCenters[existingActionCount + 1]
    : null;

  return {
    didMainLaneMove,
    didExistingActionsMoveHorizontally,
    didConnectorPathsChangeHorizontally,
    didGraphTransformChange,
    didTriggerConnectorBreak,
    newActionCenterX,
    duplicatedActionCenterX,
    newActionOnMainLane: newActionCenterX != null && after.mainLaneX != null
      ? within(newActionCenterX, after.mainLaneX)
      : null,
  };
}

export function detectFloatingCenterActionUnderBranches(steps, current) {
  if (!workflowEndsWithConditionBranch(steps)) return false;
  return (current?.mainLaneActionCardCount ?? 0) > steps.length;
}

export function captureBranchLaneContinuationDebug(container) {
  if (!container) {
    return null;
  }

  const countWithinLanes = (lanes, selector) => {
    const counts = {};
    lanes.forEach((lane) => {
      const branchId = lane.getAttribute('data-workflow-condition-branch-id') || 'unknown';
      counts[branchId] = [...lane.querySelectorAll(selector)]
        .filter((element) => !element.closest('[data-workflow-nested-condition-branch]'))
        .length;
    });
    return counts;
  };

  const topCanvas = [...container.querySelectorAll('[data-workflow-condition-branch-canvas]')]
    .find((element) => !element.closest('[data-workflow-nested-condition-branch]'));
  const topGrid = topCanvas?.querySelector('[data-workflow-condition-branch-grid]');
  const topLanes = [...(topGrid?.querySelectorAll(':scope > [data-workflow-condition-lane]') || [])];

  const branchPlusCountsByBranch = countWithinLanes(topLanes, '[data-workflow-condition-plus]');
  const branchEndCountsByBranch = countWithinLanes(topLanes, '[data-workflow-condition-end]');
  const branchConnectorSegmentCountsByBranch = countWithinLanes(
    topLanes,
    '[data-workflow-condition-lane-connector]',
  );
  const branchStepCountsByBranch = Object.fromEntries(topLanes.map((lane) => [
    lane.getAttribute('data-workflow-condition-branch-id') || 'unknown',
    Number(lane.getAttribute('data-workflow-condition-branch-step-count') || 0),
  ]));

  const terminalStepTypeByBranch = {};
  topLanes.forEach((lane) => {
    const branchId = lane.getAttribute('data-workflow-condition-branch-id') || 'unknown';
    const stepCount = Number(lane.getAttribute('data-workflow-condition-branch-step-count') || 0);
    const endsWithNestedCondition = lane.querySelector('[data-workflow-nested-condition-wrapper="true"]') != null;
    if (stepCount === 0) {
      terminalStepTypeByBranch[branchId] = 'empty';
    } else if (endsWithNestedCondition) {
      terminalStepTypeByBranch[branchId] = 'condition';
    } else {
      terminalStepTypeByBranch[branchId] = 'action';
    }
  });

  const missingPlusBranchIds = topLanes
    .filter((lane) => [...lane.querySelectorAll('[data-workflow-condition-plus]')]
      .filter((plus) => !plus.closest('[data-workflow-nested-condition-branch]')).length === 0)
    .map((lane) => lane.getAttribute('data-workflow-condition-branch-id'))
    .filter(Boolean);

  const missingEndBranchIds = topLanes
    .filter((lane) => {
      const endsWithNestedCondition = lane.querySelector('[data-workflow-nested-condition-wrapper="true"]') != null
        && Number(lane.getAttribute('data-workflow-condition-branch-step-count') || 0) > 0;
      if (endsWithNestedCondition) {
        return false;
      }
      return [...lane.querySelectorAll('[data-workflow-condition-end]')]
        .filter((end) => !end.closest('[data-workflow-nested-condition-branch]')).length === 0;
    })
    .map((lane) => lane.getAttribute('data-workflow-condition-branch-id'))
    .filter(Boolean);

  const hasFloatingCenterPlusUnderBranches = [...container.querySelectorAll('[data-workflow-condition-plus]')]
    .some((plus) => !plus.closest('[data-workflow-condition-lane]'));

  const hasFloatingCenterLineUnderBranches = [...container.querySelectorAll('[data-workflow-condition-lane-connector]')]
    .some((connector) => !connector.closest('[data-workflow-condition-lane]'));

  const hasSharedBranchMergeBar = container.querySelector(
    '[data-workflow-branch-merge], [data-workflow-condition-merge], [data-workflow-branch-continuation]',
  ) != null;

  const nestedConditionStepIds = [];
  const nestedBranchPlusCountsByBranch = {};
  const nestedBranchEndCountsByBranch = {};
  [...container.querySelectorAll('[data-workflow-nested-condition-branch]')].forEach((nestedRoot) => {
    const nestedStepId = nestedRoot.getAttribute('data-workflow-nested-condition-step-id') || 'nested';
    nestedConditionStepIds.push(nestedStepId);
    [...nestedRoot.querySelectorAll('[data-workflow-condition-lane]')].forEach((lane) => {
      const branchId = lane.getAttribute('data-workflow-condition-branch-id') || 'unknown';
      const key = `${nestedStepId}:${branchId}`;
      nestedBranchPlusCountsByBranch[key] = lane.querySelectorAll('[data-workflow-condition-plus]').length;
      nestedBranchEndCountsByBranch[key] = lane.querySelectorAll('[data-workflow-condition-end]').length;
    });
  });

  const hasUnexpectedParentEndAfterNestedCondition = [...container.querySelectorAll('[data-workflow-nested-condition-wrapper="true"]')]
    .some((wrapper) => {
      const lane = wrapper.closest('[data-workflow-condition-lane]');
      if (!lane) return false;
      return [...lane.children].some((child) => child.getAttribute('data-workflow-condition-end') != null);
    });

  return {
    branchPlusCountsByBranch,
    branchEndCountsByBranch,
    branchConnectorSegmentCountsByBranch,
    branchStepCountsByBranch,
    terminalStepTypeByBranch,
    missingPlusBranchIds,
    missingEndBranchIds,
    hasSharedBranchMergeBar,
    hasFloatingCenterPlusUnderBranches,
    hasFloatingCenterLineUnderBranches,
    hasUnexpectedParentEndAfterNestedCondition,
    nestedConditionStepIds,
    nestedBranchPlusCountsByBranch,
    nestedBranchEndCountsByBranch,
  };
}

export function buildInteractiveAddActionDebug({
  operation = 'initial',
  phase = 'initial',
  actionType = null,
  duplicatedStepId = null,
  insertedStepId = null,
  selectedPlusIndex = null,
  addedActionType = null,
  steps = [],
  container = null,
  before,
  current,
  comparison,
  pendingAddTarget = null,
  insertedActionTarget = null,
  expectedBranchCenterX = null,
}) {
  const hasUnexpectedGlobalEndUnderBranches = detectUnexpectedGlobalEndUnderBranches(container, steps);
  const expectedBranchLaneCount = workflowEndsWithConditionBranch(steps)
    ? getConditionBranchLaneCount(steps[steps.length - 1])
    : 0;
  const visibleBranchLaneCount = current?.visibleBranchLaneCount ?? 0;
  const hasMissingBranchLane = expectedBranchLaneCount > 0
    && visibleBranchLaneCount < expectedBranchLaneCount;
  const insertedActionCenterX = current?.branchActionCenters?.length
    ? current.branchActionCenters[current.branchActionCenters.length - 1]
    : comparison?.newActionCenterX ?? null;
  const hasFloatingCenterActionUnderBranches = detectFloatingCenterActionUnderBranches(steps, current);
  const didInsertIntoMainLaneByMistake = insertedActionTarget?.type === 'branch'
    && hasFloatingCenterActionUnderBranches;
  const didBranchActionAttachToCorrectLane = insertedActionTarget?.type === 'branch'
    ? expectedBranchCenterX != null && insertedActionCenterX != null
      ? Math.abs(insertedActionCenterX - expectedBranchCenterX) <= 2
      : !hasFloatingCenterActionUnderBranches
    : null;

  const branchActionCounts = workflowEndsWithConditionBranch(steps)
    ? (() => {
      const config = steps[steps.length - 1]?.config || {};
      const branchSteps = (config.branches || []).reduce((acc, branch) => ({
        ...acc,
        [branch.id]: (branch.steps || []).length,
      }), {});
      return {
        ...branchSteps,
        none: (config.noneBranch?.steps || []).length,
      };
    })()
    : {};

  const branchPath = pendingAddTarget?.parentPath
    ?? insertedActionTarget?.parentPath
    ?? [];
  const branchConnectorSegmentCounts = current?.branchConnectorSegmentCounts ?? {};
  const branchPlusCounts = current?.branchPlusCounts ?? {};
  const isEndBelowLastBranchStep = Object.entries(branchConnectorSegmentCounts).every(([branchId, connectorCount]) => {
    const stepCount = branchActionCounts[branchId] ?? 0;
    const terminalType = current?.terminalStepTypeByBranch?.[branchId];
    const expectedConnectors = stepCount === 0
      ? 1
      : (terminalType === 'condition' ? stepCount : stepCount + 1);
    return connectorCount === expectedConnectors;
  });
  const didBranchConnectorExtend = Object.entries(branchActionCounts).some(([branchId, count]) => (
    count > 0 && (branchConnectorSegmentCounts[branchId] ?? 0) >= count + 1
  ));
  const nestedExpectedBranchLaneCount = 2;
  const nestedHasMissingBranchLane = (current?.nestedVisibleBranchLaneCount ?? 0) > 0
    && (current?.nestedVisibleBranchLaneCount ?? 0) < nestedExpectedBranchLaneCount;
  const nestedHasUnexpectedGlobalEnd = container
    ? [...container.querySelectorAll('[data-workflow-nested-condition-branch]')].some(
      (element) => element.querySelector(':scope > [data-workflow-condition-end]') != null,
    )
    : false;

  return {
    scenario: 'interactive-add-action-behavior',
    operation,
    phase,
    actionType,
    duplicatedStepId,
    insertedStepId,
    selectedPlusIndex,
    addedActionType,
    pendingAddTarget,
    insertedActionTarget,
    branchPath,
    branchId: insertedActionTarget?.branchId ?? pendingAddTarget?.branchId ?? null,
    nestedConditionStepId: insertedActionTarget?.conditionStepId ?? pendingAddTarget?.conditionStepId ?? null,
    expectedBranchLaneCount,
    visibleBranchLaneCount,
    branchGraphWidth: current?.branchGraphWidth ?? null,
    branchGridWidth: current?.branchGraphWidth ?? null,
    branchLaneWidths: current?.branchLaneWidths ?? [],
    effectiveBranchLaneWidths: current?.effectiveBranchLaneWidths ?? [],
    expectedGraphWidthFromLanes: current?.expectedGraphWidthFromLanes ?? null,
    branchLaneWidth: current?.branchGraphWidth && expectedBranchLaneCount
      ? current.branchGraphWidth / expectedBranchLaneCount
      : null,
    branchCenters: current?.branchCenters ?? [],
    branchLaneCenters: current?.branchCenters ?? [],
    nestedGraphWidthsByBranch: current?.nestedGraphWidthsByBranch ?? {},
    widestNestedGraphWidthByBranch: current?.widestNestedGraphWidthByBranch ?? {},
    hasBranchLaneWidthTooSmall: current?.hasBranchLaneWidthTooSmall ?? false,
    hasNestedGraphOverlap: current?.hasNestedGraphOverlap ?? false,
    hasNestedGraphClipping: current?.hasNestedGraphClipping ?? false,
    graphLeft: current?.branchGraphLeft ?? null,
    parentCenterX: current?.parentCenterX ?? null,
    sourceXLocal: current?.parentCenterX ?? null,
    targetXLocal: current?.branchCenters?.[0] ?? null,
    hasMissingBranchLane,
    mainLaneXBefore: before?.mainLaneX ?? null,
    mainLaneXCurrent: current?.mainLaneX ?? null,
    mainChainWidth: current?.mainChainWidth ?? current?.mainLaneWidth ?? null,
    triggerRowVisualWidth: current?.triggerRowVisualWidth ?? null,
    triggerRowVisualCenterX: current?.triggerRowVisualCenterX ?? null,
    actionLaneWidth: current?.actionLaneWidth ?? WORKFLOW_MAIN_CARD_WIDTH,
    actionLaneCenterX: current?.actionLaneCenterX ?? WORKFLOW_MAIN_CARD_CENTER_X,
    triggerMergeCenterX: current?.triggerMergeCenterX ?? null,
    triggerTrunkEndX: current?.triggerTrunkEndX ?? null,
    firstStepConnectorCenterX: current?.firstStepConnectorCenterX ?? null,
    triggerToMainDelta: current?.triggerToMainDelta ?? null,
    didTriggerConnectorDetach: current?.didTriggerConnectorDetach ?? false,
    didTriggerConnectorUseSqueezedWidth: current?.didTriggerConnectorUseSqueezedWidth ?? false,
    didMainLaneShrinkBelowCardWidth: current?.didMainLaneShrinkBelowCardWidth ?? false,
    graphTransform: current?.graphTransformX ?? 0,
    triggerMergeCenterXBefore: before?.triggerMergeCenterX ?? null,
    triggerMergeCenterXCurrent: current?.triggerMergeCenterX ?? null,
    triggerCentersBefore: before?.triggerCenters ?? [],
    triggerCentersCurrent: current?.triggerCenters ?? [],
    existingActionCentersBefore: before?.actionCenters ?? [],
    existingActionCentersCurrent: current?.actionCenters ?? [],
    newActionCenterX: comparison?.newActionCenterX ?? null,
    duplicatedActionCenterX: comparison?.duplicatedActionCenterX ?? null,
    insertedActionCenterX,
    insertedActionBranchId: insertedActionTarget?.branchId ?? null,
    expectedBranchCenterX,
    plusCentersBefore: before?.plusCenters ?? [],
    plusCentersCurrent: current?.plusCenters ?? [],
    connectorPathsHashBefore: before?.connectorPathsHash ?? '',
    connectorPathsHashCurrent: current?.connectorPathsHash ?? '',
    graphTransformBefore: before?.graphTransformX ?? 0,
    graphTransformCurrent: current?.graphTransformX ?? 0,
    graphRootWidthBefore: before?.graphRootWidth ?? 0,
    graphRootWidthCurrent: current?.graphRootWidth ?? 0,
    branchEndCount: current?.branchEndCount ?? 0,
    branchEndPositions: current?.branchEndPositions ?? [],
    branchActionCounts,
    branchConnectorSegmentCounts,
    branchPlusCounts,
    branchLaneHeights: current?.branchLaneHeights ?? {},
    branchIds: current?.branchIds ?? [],
    isEndBelowLastBranchStep,
    didBranchConnectorExtend,
    nestedExpectedBranchLaneCount,
    nestedVisibleBranchLaneCount: current?.nestedVisibleBranchLaneCount ?? 0,
    nestedBranchGraphWidth: current?.nestedBranchGraphWidth ?? 0,
    nestedBranchGraphHeight: current?.nestedBranchGraphHeight ?? 0,
    nestedGraphLeft: current?.nestedGraphLeft ?? null,
    nestedParentCenterX: current?.nestedParentCenterX ?? null,
    nestedSourceXLocal: current?.nestedSourceXLocal ?? current?.nestedParentCenterX ?? null,
    nestedTargetXLocalValues: current?.nestedTargetXLocalValues ?? [],
    nestedGraphOverflowMode: current?.nestedGraphOverflowMode ?? null,
    nestedWrapperOverflowMode: current?.nestedWrapperOverflowMode ?? null,
    parentBranchOverflowMode: current?.parentBranchOverflowMode ?? null,
    parentBranchLaneWidth: current?.parentBranchLaneWidth ?? 0,
    nestedIsClipped: current?.nestedIsClipped ?? false,
    nestedIsHorizontallyClipped: current?.nestedIsHorizontallyClipped ?? false,
    nestedLeftEdgeVisible: current?.nestedLeftEdgeVisible ?? false,
    nestedRightEdgeVisible: current?.nestedRightEdgeVisible ?? false,
    parentBranchLaneHeight: current?.parentBranchLaneHeight ?? 0,
    parentBranchSpacerHeight: current?.parentBranchSpacerHeight ?? 0,
    parentBranchId: current?.parentBranchId ?? null,
    hasDuplicateParentEndUnderNestedCondition: current?.hasDuplicateParentEndUnderNestedCondition ?? false,
    isParentEndBelowNestedGraph: current?.isParentEndBelowNestedGraph ?? true,
    nestedHasMissingBranchLane,
    nestedHasUnexpectedGlobalEnd,
    didNestedConditionRenderBranchGraph: current?.didNestedConditionRenderBranchGraph ?? false,
    didNestedBranchActionAttachToCorrectLane: insertedActionTarget?.type === 'branch'
      && branchPath.length > 0
      ? didBranchActionAttachToCorrectLane
      : null,
    hasGlobalMainChainEnd: current?.hasGlobalMainChainEnd ?? false,
    didMainLaneMove: comparison?.didMainLaneMove ?? false,
    didTriggerConnectorBreak: comparison?.didTriggerConnectorBreak ?? false,
    didExistingActionsMoveHorizontally: comparison?.didExistingActionsMoveHorizontally ?? false,
    didConnectorPathsChangeHorizontally: comparison?.didConnectorPathsChangeHorizontally ?? false,
    didGraphTransformChange: comparison?.didGraphTransformChange ?? false,
    hasUnexpectedGlobalEndUnderBranches,
    didInsertIntoMainLaneByMistake,
    didInsertIntoWrongBranch: insertedActionTarget?.type === 'branch'
      && expectedBranchCenterX != null
      && insertedActionCenterX != null
      && Math.abs(insertedActionCenterX - expectedBranchCenterX) > 2,
    didBranchActionAttachToCorrectLane,
    hasFloatingCenterActionUnderBranches,
    branchPlusCountsByBranch: current?.branchPlusCountsByBranch ?? {},
    branchEndCountsByBranch: current?.branchEndCountsByBranch ?? {},
    branchStepCountsByBranch: current?.branchStepCountsByBranch ?? {},
    terminalStepTypeByBranch: current?.terminalStepTypeByBranch ?? {},
    hasSharedBranchMergeBar: current?.hasSharedBranchMergeBar ?? false,
    hasFloatingCenterPlusUnderBranches: current?.hasFloatingCenterPlusUnderBranches ?? false,
    hasFloatingCenterLineUnderBranches: current?.hasFloatingCenterLineUnderBranches ?? false,
    hasUnexpectedParentEndAfterNestedCondition: current?.hasUnexpectedParentEndAfterNestedCondition ?? false,
    missingPlusBranchIds: current?.missingPlusBranchIds ?? [],
    missingEndBranchIds: current?.missingEndBranchIds ?? [],
    nestedBranchPlusCountsByBranch: current?.nestedBranchPlusCountsByBranch ?? {},
    nestedBranchEndCountsByBranch: current?.nestedBranchEndCountsByBranch ?? {},
  };
}
